<template>
  <v-menu
    v-if="isLoggedIn && isEphemeralSession"
    v-model="menuOpen"
    offset-y
    :close-on-content-click="false"
    left
    min-width="200"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-card
        outlined
        class="mx-2 px-3 d-flex align-center"
        v-bind="attrs"
        v-on="on"
        link
        style="border-color: #fb8c00; height: 36px"
      >
        <v-progress-circular
          :value="progressPercentage"
          :size="24"
          :width="3"
          color="orange"
          class="mr-2"
        >
          <v-icon x-small color="orange">mdi-timer-sand</v-icon>
        </v-progress-circular>
        <span class="text-caption font-weight-medium orange--text">
          {{ formatTime(timeRemaining) }}
        </span>
      </v-card>
    </template>

    <v-card>
      <v-card-text>
        <div class="text-caption mb-2">
          Diese Sitzung wurde über einen QR-Code gestartet und wird automatisch
          beendet.
        </div>
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="text-caption font-weight-medium">Verbleibende Zeit</span>
          <span class="text-caption font-weight-bold orange--text">
            {{ formatTime(timeRemaining) }}
          </span>
        </div>
        <v-progress-linear
          :value="progressPercentage"
          color="orange"
          height="4"
          rounded
          class="mb-1"
        ></v-progress-linear>
      </v-card-text>
      <v-card-actions class="mt-n2 mr-2 pb-2">
        <v-spacer></v-spacer>
        <v-btn color="error" text @click="signOut" :loading="signingOut">
          <v-icon left small>mdi-logout-variant</v-icon>
          Sofort abmelden
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { auth } from "@/firebase.js";
import { USER } from "@/store/modules.js";
import { SIGN_OUT } from "@/store/action-types.js";

export default {
  name: "QRSessionManager",
  data() {
    return {
      timeRemaining: 0,
      totalTime: 0,
      expiresAt: null,
      countdownInterval: null,
      signingOut: false,
      isEphemeralSession: false,
      isLoggedIn: false,
      unsubscribeAuth: null,
      menuOpen: false,
    };
  },
  computed: {
    progressPercentage() {
      if (!this.totalTime) return 0;
      return (this.timeRemaining / this.totalTime) * 100;
    },
  },
  created() {
    // Listen for auth state changes reactively
    this.unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      this.isLoggedIn = !!user;
      if (user) {
        await this.checkForEphemeralSession();
      } else {
        this.isEphemeralSession = false;
        this.stopCountdown();
      }
    });
  },
  methods: {
    async checkForEphemeralSession() {
      if (!auth.currentUser) return;

      try {
        const idTokenResult = await auth.currentUser.getIdTokenResult();
        const expiresAt = idTokenResult.claims.ephemeralExpiresAt;

        if (!expiresAt) return;

        // Session already expired — sign out immediately
        if (Date.now() >= expiresAt) {
          await this.performSignOut();
          return;
        }

        this.expiresAt = expiresAt;
        this.totalTime = Math.floor((expiresAt - Date.now()) / 1000);
        this.timeRemaining = this.totalTime;
        this.isEphemeralSession = true;
        this.startCountdown();
      } catch (e) {
        console.error("Failed to check ephemeral session:", e);
      }
    },

    startCountdown() {
      this.stopCountdown();
      this.countdownInterval = setInterval(() => {
        this.timeRemaining = Math.max(
          0,
          Math.floor((this.expiresAt - Date.now()) / 1000),
        );

        if (this.timeRemaining <= 0) {
          this.performSignOut();
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    async signOut() {
      this.signingOut = true;
      await this.performSignOut();
    },

    async performSignOut() {
      this.stopCountdown();
      try {
        // Use Vuex SIGN_OUT action to clear state and redirect
        await this.$store.dispatch(`${USER}${SIGN_OUT}`);
        this.isEphemeralSession = false;
      } catch (error) {
        console.error("Error signing out:", error);
      } finally {
        this.signingOut = false;
      }
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    },
  },

  beforeDestroy() {
    this.stopCountdown();

    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
      this.unsubscribeAuth = null;
    }
  },
};
</script>
